// frontend/hooks/useAnonymousSession.js
import { useState, useEffect, useCallback } from 'react';
import { getOrCreateSessionId } from '../utils/deviceFingerprint';

const DAILY_MESSAGE_LIMIT = 3;

export function useAnonymousSession() {
  const [sessionInfo, setSessionInfo] = useState(null);
  const [messageCount, setMessageCount] = useState(0);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [hoursUntilReset, setHoursUntilReset] = useState(0);

  // Check if user is authenticated
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const user = userData ? JSON.parse(userData) : null;
    const userIsAnonymous = !user || !user.token;
    
    setIsAnonymous(userIsAnonymous);
    
    if (userIsAnonymous) {
      // Get or create session ID
      const { sessionId, deviceFingerprint } = getOrCreateSessionId();
      setSessionInfo({ sessionId, deviceFingerprint });
      
      // Load message count from localStorage
      const storedCount = localStorage.getItem('leebai_anonymous_message_count');
      const storedResetTime = localStorage.getItem('leebai_anonymous_reset_time');
      
      if (storedCount && storedResetTime) {
        const resetTime = new Date(storedResetTime);
        const now = new Date();
        
        // Check if 24 hours have passed
        if (now >= resetTime) {
          // Reset count
          setMessageCount(0);
          localStorage.removeItem('leebai_anonymous_message_count');
          localStorage.removeItem('leebai_anonymous_reset_time');
        } else {
          // Calculate hours until reset
          const hoursUntil = Math.ceil((resetTime - now) / (1000 * 60 * 60));
          setHoursUntilReset(hoursUntil);
          setMessageCount(parseInt(storedCount, 10));
        }
      }
    } else {
      // Clear anonymous session data when user logs in
      setSessionInfo(null);
      setMessageCount(0);
      localStorage.removeItem('leebai_anonymous_message_count');
      localStorage.removeItem('leebai_anonymous_reset_time');
    }
  }, []);

  // Update message count when limit is reached
  const handleLimitReached = useCallback((response) => {
    if (response?.code === 'DAILY_LIMIT_REACHED') {
      setMessageCount(response.used || DAILY_MESSAGE_LIMIT);
      if (response.hoursUntilReset) {
        setHoursUntilReset(response.hoursUntilReset);
      }
    }
  }, []);

  // Increment message count (called after successful message)
  const incrementMessageCount = useCallback(() => {
    if (!isAnonymous) return;
    
    const newCount = messageCount + 1;
    setMessageCount(newCount);
    
    // Set reset time (24 hours from now)
    const resetTime = new Date();
    resetTime.setHours(resetTime.getHours() + 24);
    
    localStorage.setItem('leebai_anonymous_message_count', newCount.toString());
    localStorage.setItem('leebai_anonymous_reset_time', resetTime.toISOString());
    
    // Calculate hours until reset
    const hoursUntil = Math.ceil((resetTime - new Date()) / (1000 * 60 * 60));
    setHoursUntilReset(hoursUntil);
  }, [isAnonymous, messageCount]);

  // Get remaining messages
  const remainingMessages = Math.max(0, DAILY_MESSAGE_LIMIT - messageCount);
  const isLimitReached = messageCount >= DAILY_MESSAGE_LIMIT;

  return {
    isAnonymous,
    sessionInfo,
    messageCount,
    remainingMessages,
    isLimitReached,
    hoursUntilReset,
    dailyLimit: DAILY_MESSAGE_LIMIT,
    handleLimitReached,
    incrementMessageCount,
  };
}

